import React, { useState, useEffect } from 'react'
import { Check, Search } from 'lucide-react'
import Popover from '../../../../components/ui/popover';
import { AVAILABLE_EMOTIONS } from './constants';
import { cn } from '../../../../lib/utils';

interface EmotionPopoverProps {
    currentEmotion?: string
    options?: string[]
    onSelect: (emotion: string) => void
    open: boolean
    onOpenChange: (open: boolean) => void
    children: React.ReactNode

    // Controlled mode props
    variant?: 'default' | 'command' // 'default' = with search input & internal state. 'command' = external control (autocomplete)
    selectedIndex?: number // for 'command' mode
}

export const EmotionPopover: React.FC<EmotionPopoverProps> = ({
    currentEmotion,
    options = AVAILABLE_EMOTIONS,
    onSelect,
    open,
    onOpenChange,
    children,
    variant = 'default',
    selectedIndex = 0
}) => {
    // Internal state for 'default' mode
    const [search, setSearch] = useState('')
    const [internalActiveIndex, setInternalActiveIndex] = useState(0)

    const filtered = variant === 'default'
        ? options.filter(e => e.toLowerCase().includes(search.toLowerCase()))
        : options // In command mode, options are already filtered by parent

    // Reset active index when filtered list changes (only default mode)
    useEffect(() => {
        if (variant === 'default') setInternalActiveIndex(0)
    }, [search, variant])

    const activeIndex = variant === 'default' ? internalActiveIndex : selectedIndex

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (variant !== 'default') return // Parent handles keys in command mode

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setInternalActiveIndex(prev => (prev + 1) % filtered.length)
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setInternalActiveIndex(prev => (prev - 1 + filtered.length) % filtered.length)
        } else if (e.key === 'Enter') {
            e.preventDefault()
            if (filtered[activeIndex]) {
                onSelect(filtered[activeIndex])
                onOpenChange(false)
                setSearch('')
            }
        }
    }

    return (
        <Popover
            open={open}
            onOpenChange={onOpenChange}
            trigger={({ ref, onClick, onMouseEnter, onMouseLeave }) => (
                <div
                    ref={ref}
                    onClick={onClick}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    className="inline-block"
                >
                    {children}
                </div>
            )}
        >
            <div className="w-[240px] p-1 flex flex-col">
                {variant === 'default' && (
                    <div className="flex items-center border-b border-black/5 px-3 pb-2 pt-2 mb-1">
                        <Search className="mr-2 h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
                        <input
                            className="flex h-8 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/70 disabled:cursor-not-allowed disabled:opacity-50"
                            placeholder="Find an emotion..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                            autoFocus
                        />
                    </div>
                )}
                <div className="max-h-[280px] overflow-y-auto px-1 py-1 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                    {filtered.length === 0 && (
                        <div className="py-8 text-center text-xs text-muted-foreground/60 select-none">
                            No emotions found.
                        </div>
                    )}
                    {filtered.map((emotion, idx) => (
                        <div
                            key={emotion}
                            className={cn(
                                "flex cursor-pointer select-none items-center rounded-md px-2.5 py-1.5 text-sm transition-colors outline-none mx-0.5",
                                "hover:bg-brand-50 hover:text-brand-900",
                                currentEmotion === emotion && "bg-brand-50 text-brand-900 font-medium",
                                activeIndex === idx && "bg-brand-50 text-brand-900 ring-1 ring-brand-200/50"
                            )}
                            onClick={(e) => {
                                e.stopPropagation()
                                onSelect(emotion)
                                onOpenChange(false)
                                if (variant === 'default') setSearch('')
                            }}
                        >
                            <span className="capitalize flex-1 truncate">{emotion}</span>
                            {currentEmotion === emotion && (
                                <Check className="ml-2 h-3.5 w-3.5 text-brand-600" />
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </Popover>
    )
}